import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormikWrapper } from '@uncinc/uncinc-react-kitchen-sink';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import {
  UI_OPERATION_STATUS_REQUEST,
} from '@uncinc/react-drupal-core';

import useTypedDispatch from '../../../hooks/useTypedDispatch';
import { getRegistrationData } from '../../../store/actions/Registrations';
import { userUpdate } from '../../../store/actions/User';
import { MINIMUM_PASSWORD_CHARACTER_COUNT, ROUTE_TYPE_SELF_HELP } from '../../../config/constants';
import Page from '../../../containers/Page';
import useClasses from '../../../hooks/useClasses';
import useOperationStatus from '../../../hooks/useOperationStatus';
import useRoutes from '../../../hooks/useRoutes';
import useToast from '../../../hooks/useToast';
import {
  Button,
  CoreLink,
  Main,
  PasswordField,
} from '../../../components';

import './style.scss';

const PageAccountChangePassword = () => {
  const { buildClass, joinClasses } = useClasses('PageAccountChangePassword');
  const { t } = useTranslation();
  const dispatch = useTypedDispatch();
  const changePasswordOperationStatus = useOperationStatus('user', 'update');
  const currentUserId = useSelector((state) => state.getIn(['login', 'uid'], ''));
  const { account } = useRoutes(ROUTE_TYPE_SELF_HELP);
  const toast = useToast();

  const validationSchema = Yup.object().shape({
    password: Yup
      .string()
      .required(t('pages.account.change_password.fields.password.validation.required')),
    new_password: Yup.string()
      .min(MINIMUM_PASSWORD_CHARACTER_COUNT, t('pages.account.change_password.fields.new_password.validation.minimum', { count: MINIMUM_PASSWORD_CHARACTER_COUNT }))
      .matches(/^(?=.*\d)(?=.*[a-zA-Z]).*$/, t('pages.reset_password.fields.password.validation.invalid'))
      .required(t('pages.account.change_password.fields.new_password.validation.required')),
    new_password_confirm: Yup
      .string()
      .required(t('pages.account.change_password.fields.new_password_confirm.validation.required'))
      .oneOf([Yup.ref('new_password'), null], t('pages.account.change_password.fields.new_password_confirm.validation.no_match')),
  });

  const onSubmit = async (values) => {
    const { password, new_password } = values;
    await dispatch(userUpdate(currentUserId, {
      pass: [{
        existing: password,
        value: new_password,
      }],
    })).then(() => {
      dispatch(getRegistrationData());
      toast.show(t('pages.account.change_password.success_message'), 'success');
    })
      .catch((err) => {
        toast.show(t('pages.account.change_password.error_message'), 'error');
        console.error(err);
      });
  };

  return (
    <Page className={joinClasses(buildClass())} title={t('pages.account.change_password.title')}>
      <Main className="col-md-8 col-lg-6 offset-md-2 offset-lg-1 py-8 py-lg-12">
        <header className={buildClass('header')}>
          <CoreLink
            to={account.base}
            title={t('buttons.back')}
            className="icon-before icon-chevron icon-orientation-left"
          />
        </header>

        <h1 className={buildClass('title')}>{t('pages.account.change_password.title')}</h1>
        <FormikWrapper
          initialValues={{ password: '', new_password: '', new_password_confirm: '' }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            onSubmit(values);
            setSubmitting(false);
          }}
          validateOnChange={false}
          validateOnBlur={false}
        >
          <PasswordField
            fieldName="password"
            title={t("pages.account.change_password.fields.password.label")}
          />
          <PasswordField
            fieldName="new_password"
            title={t("pages.account.change_password.fields.new_password.label")}
          />
          <PasswordField
            fieldName="new_password_confirm"
            title={t("pages.account.change_password.fields.new_password_confirm.label")}
          />
          <Button
            htmlType="submit"
            buttonState={changePasswordOperationStatus === UI_OPERATION_STATUS_REQUEST && 'disabled'}
          >
            {t("buttons.save")}
          </Button>
        </FormikWrapper>
      </Main>
    </Page>
  );
};

export default PageAccountChangePassword;
